/**
 * CampaignQueueHealthAlerts - Alertas de saúde da fila de campanhas
 * 
 * Exibe alertas quando há problemas identificados na fila
 */

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, AlertCircle, Info } from 'lucide-react';
import type { CampaignQueueAlert } from '@/hooks/useCampaignQueueMonitor';

interface CampaignQueueHealthAlertsProps {
  alerts: CampaignQueueAlert[] | undefined;
  isLoading: boolean;
}

export function CampaignQueueHealthAlerts({ alerts, isLoading }: CampaignQueueHealthAlertsProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (!alerts || alerts.length === 0) {
    return null;
  }

  // Mapear tipo de alerta para variante e ícone
  const getAlertConfig = (type: CampaignQueueAlert['type']) => {
    switch (type) {
      case 'error':
        return {
          variant: 'destructive' as const,
          icon: AlertCircle
        };
      case 'warning':
        return {
          variant: 'default' as const,
          icon: AlertTriangle
        };
      default:
        return {
          variant: 'default' as const,
          icon: Info
        };
    }
  };

  return (
    <div className="space-y-2">
      {alerts.map((alert, index) => {
        const config = getAlertConfig(alert.type);
        const Icon = config.icon;
        
        return (
          <Alert key={index} variant={config.variant}>
            <Icon className="h-4 w-4" />
            <AlertTitle>{alert.title}</AlertTitle>
            <AlertDescription>{alert.message}</AlertDescription>
          </Alert>
        );
      })}
    </div>
  );
}
